import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatDate, formatEUR } from "@/lib/dict";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { usePickLists } from "@/hooks/usePickLists";

const POLICY_FAMILIES = { DO: "Dommages-Ouvrage", multirisk: "Multirisque", liability: "Responsabilité civile", machinery: "Bris de machine" };

export default function Policies() {
  const qc = useQueryClient();
  const pl = usePickLists();
  const { data: policies = [] } = useQuery({ queryKey: ["policies"], queryFn: () => api.get("/policies").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const { data: insurers = [] } = useQuery({ queryKey: ["contacts", "insurer"], queryFn: () => api.get("/contacts?type=insurer").then((r) => r.data) });
  const { data: brokers = [] } = useQuery({ queryKey: ["contacts", "broker"], queryFn: () => api.get("/contacts?type=broker").then((r) => r.data) });
  const [dialog, setDialog] = useState(null);
  const am = Object.fromEntries(assets.map((a) => [a.id, a]));

  const create = useMutation({
    mutationFn: (d) => api.post("/policies", d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["policies"] }); toast.success("Police créée"); setDialog(null); },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/policies/${id}`, patch),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["policies"] }); toast.success("Police mise à jour"); setDialog(null); },
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/policies/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["policies"] }); toast.success("Police supprimée"); },
  });

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Polices d'assurance</h1>
          <p className="text-sm text-slate-500 mt-1.5">{policies.length} police(s) actives</p>
        </div>
        <Button data-testid="new-policy-btn" className="text-white" style={{ background: "#023E73" }} onClick={() => setDialog({ mode: "create" })}>
          <Plus size={16} className="mr-1.5" /> Nouvelle police
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {policies.map((p) => (
          <Card key={p.id} data-testid={`policy-${p.id}`} className="p-5 border-slate-200 group hover:shadow-md transition">
            <div className="flex items-start justify-between">
              <div className="font-mono text-xs text-slate-500">{p.policy_number}</div>
              <div className="flex items-center gap-1">
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-slate-100">{pl.labelOf("policy.policy_family", p.policy_family, POLICY_FAMILIES)}</span>
                <button data-testid={`edit-policy-${p.id}`} onClick={() => setDialog({ mode: "edit", policy: p })} className="p-1 rounded hover:bg-slate-100 text-slate-600 opacity-0 group-hover:opacity-100 transition"><Pencil size={12} /></button>
                <button data-testid={`delete-policy-${p.id}`} onClick={() => { if (window.confirm(`Supprimer ${p.policy_number} ?`)) remove.mutate(p.id); }} className="p-1 rounded hover:bg-red-50 text-red-600 opacity-0 group-hover:opacity-100 transition"><Trash2 size={12} /></button>
              </div>
            </div>
            <div className="font-display font-semibold text-base text-slate-900 mt-2">{am[p.asset_id]?.name || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">Assureur : {p.insurer} • Courtier : {p.broker || "—"}</div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-xs">
              <div><div className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Plafond</div><div className="font-mono font-semibold">{formatEUR(p.coverage_limit)}</div></div>
              <div><div className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Franchise</div><div className="font-mono font-semibold">{formatEUR(p.deductible)}</div></div>
              <div><div className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Début</div><div>{formatDate(p.start_date)}</div></div>
              <div><div className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Fin</div><div>{formatDate(p.end_date)}</div></div>
            </div>
            {p.guarantees && <div className="mt-3 text-xs text-slate-600 border-t border-slate-100 pt-3 line-clamp-2">{p.guarantees}</div>}
          </Card>
        ))}
        {policies.length === 0 && <div className="text-sm text-slate-400 col-span-full text-center py-10">Aucune police pour le moment</div>}
      </div>

      {dialog && (
        <PolicyFormDialog
          mode={dialog.mode}
          policy={dialog.policy}
          assets={assets}
          insurers={insurers}
          brokers={brokers}
          onClose={() => setDialog(null)}
          onSubmit={(data) => (dialog.mode === "create" ? create.mutate(data) : update.mutate({ id: dialog.policy.id, patch: data }))}
        />
      )}
    </div>
  );
}

function PolicyFormDialog({ mode, policy, assets, insurers, brokers, onClose, onSubmit }) {
  const pl = usePickLists();
  const [form, setForm] = useState({
    policy_number: policy?.policy_number || "",
    policy_family: policy?.policy_family || "multirisk",
    asset_id: policy?.asset_id || "",
    insurer: policy?.insurer || "",
    broker: policy?.broker || "",
    start_date: policy?.start_date?.slice(0, 10) || "",
    end_date: policy?.end_date?.slice(0, 10) || "",
    deductible: policy?.deductible ?? 0,
    coverage_limit: policy?.coverage_limit ?? 0,
    guarantees: policy?.guarantees || "",
    exclusions: policy?.exclusions || "",
    notes: policy?.notes || "",
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{mode === "create" ? "Nouvelle police d'assurance" : "Modifier la police"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>N° de police</Label><Input data-testid="policy-number" value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} /></div>
            <div><Label>Famille</Label>
              <Select value={form.policy_family} onValueChange={(v) => setForm({ ...form, policy_family: v })}>
                <SelectTrigger data-testid="policy-family"><SelectValue /></SelectTrigger>
                <SelectContent>{pl.options("policy.policy_family", POLICY_FAMILIES).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Actif</Label>
            <Select value={form.asset_id} onValueChange={(v) => setForm({ ...form, asset_id: v })}>
              <SelectTrigger data-testid="policy-asset"><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Assureur</Label>
              {insurers.length > 0 ? (
                <Select value={form.insurer} onValueChange={(v) => setForm({ ...form, insurer: v })}>
                  <SelectTrigger data-testid="policy-insurer"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>{insurers.map((i) => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid="policy-insurer" value={form.insurer} onChange={(e) => setForm({ ...form, insurer: e.target.value })} placeholder="AXA Pro" />
              )}
            </div>
            <div><Label>Courtier</Label>
              {brokers.length > 0 ? (
                <Select value={form.broker} onValueChange={(v) => setForm({ ...form, broker: v })}>
                  <SelectTrigger data-testid="policy-broker"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>{brokers.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid="policy-broker" value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })} placeholder="Gras Savoye" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date début</Label><Input type="date" data-testid="policy-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Date fin</Label><Input type="date" data-testid="policy-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Franchise (€)</Label><Input type="number" data-testid="policy-deductible" value={form.deductible} onChange={(e) => setForm({ ...form, deductible: Number(e.target.value) })} /></div>
            <div><Label>Plafond (€)</Label><Input type="number" data-testid="policy-coverage" value={form.coverage_limit} onChange={(e) => setForm({ ...form, coverage_limit: Number(e.target.value) })} /></div>
          </div>
          <div><Label>Garanties couvertes</Label><Textarea rows={2} value={form.guarantees} onChange={(e) => setForm({ ...form, guarantees: e.target.value })} /></div>
          <div><Label>Exclusions</Label><Textarea rows={2} value={form.exclusions} onChange={(e) => setForm({ ...form, exclusions: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button data-testid="policy-submit" onClick={() => onSubmit(form)} disabled={!form.policy_number || !form.asset_id} className="text-white" style={{ background: "#023E73" }}>
            {mode === "create" ? "Créer" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
